import React, { useState, useEffect, useRef } from 'react';
import {
  Row, Col, Card, Table, Tag, Typography, Space, Button,
  Tabs, Badge, Statistic, Switch, Tooltip, message, Select
} from 'antd';
import {
  ApiOutlined, ReloadOutlined, WifiOutlined, PlusOutlined,
  CopyOutlined, EditOutlined, DeleteOutlined, ThunderboltOutlined,
  CheckCircleOutlined, StopOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getReaders, getLiveStats, getRawScans, getEvents } from '../api';

const { Title, Text } = Typography;
const { Option } = Select;

function RssiBar({ value }) {
  if (value === null || value === undefined) return <Typography.Text type="secondary">—</Typography.Text>;
  const rssi = Number(value);
  const color = rssi > -60 ? '#22c55e' : rssi > -75 ? '#f97316' : '#ef4444';
  const pct   = Math.min(100, Math.max(0, ((rssi + 100) / 60) * 100));
  return (
    <Tooltip title={`${rssi} dBm`}>
      <Space size={6} align="center">
        <div style={{ width: 60, height: 8, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.3s' }} />
        </div>
        <Typography.Text style={{ fontSize: 12, color }}>{rssi}</Typography.Text>
      </Space>
    </Tooltip>
  );
}

export default function ReaderAPI() {
  const navigate = useNavigate();
  const [activeTab,    setActiveTab]    = useState('endpoints');
  const [readers,      setReaders]      = useState([]);
  const [scans,        setScans]        = useState([]);
  const [stats,        setStats]        = useState({ total_scans_today: 0, unique_tags_today: 0, active_readers: 0 });
  const [events,       setEvents]       = useState([]);
  const [filterEvent,  setFilterEvent]  = useState(null);
  const [autoRefresh,  setAutoRefresh]  = useState(false);
  const [streaming,    setStreaming]     = useState(false);
  const [loadingR,     setLoadingR]     = useState(false);
  const [loadingS,     setLoadingS]     = useState(false);
  const intervalRef = useRef(null);

  const loadReaders = async () => {
    setLoadingR(true);
    try {
      const params = {};
      if (filterEvent) params.event_id = filterEvent;
      const r = await getReaders(params);
      setReaders(r.data.data || []);
    } catch (_) {}
    setLoadingR(false);
  };

  const loadScans = async () => {
    setLoadingS(true);
    try {
      const params = { limit: 50 };
      if (filterEvent) params.event_id = filterEvent;
      const sr = await getRawScans(params);
      const scanList = sr.data.data || [];
      setScans(scanList);
      // Compute stats from scan data
      const uniqueTags = new Set(scanList.map(s => s.rfid_tag)).size;
      if (filterEvent) {
        try {
          const stR = await getLiveStats({ event_id: filterEvent });
          const d = stR.data.data || {};
          setStats({
            total_scans_today:  d.scans_last_hour || scanList.length,
            unique_tags_today:  uniqueTags,
            active_readers:     d.readers_online || 0,
          });
        } catch (_) {
          setStats({ total_scans_today: scanList.length, unique_tags_today: uniqueTags, active_readers: 0 });
        }
      } else {
        setStats({ total_scans_today: scanList.length, unique_tags_today: uniqueTags, active_readers: 0 });
      }
    } catch (_) {}
    setLoadingS(false);
  };

  useEffect(() => {
    getEvents().then(r => {
      const evList = r.data.data || [];
      setEvents(evList);
      const active = evList.find(e => e.status === 'active');
      if (active) setFilterEvent(active.id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadReaders();
    loadScans();
  }, [filterEvent]); // eslint-disable-line

  // Auto-refresh / streaming
  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRefresh || streaming) {
      intervalRef.current = setInterval(() => {
        loadScans();
        if (activeTab === 'endpoints') loadReaders();
      }, streaming ? 2000 : 5000);
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [autoRefresh, streaming, filterEvent, activeTab]); // eslint-disable-line

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url).then(() => message.success('URL copied!'));
  };

  // ── Reader Endpoints columns ──────────────────────────────────────
  const readerCols = [
    {
      title: 'NAME', dataIndex: 'reader_name', render: (v, r) => (
        <div>
          <Text strong style={{ fontSize: 12 }}>{v}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 11 }}>{r.reader_code}</Text>
        </div>
      )
    },
    { title: 'IP ADDRESS', dataIndex: 'ip_address', width: 130, render: v => <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{v || '—'}</Text> },
    { title: 'PORT', width: 70, render: (_, r) => <Text style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.connectivity === 'LAN' ? '5505' : '5000'}</Text> },
    {
      title: 'PROTOCOL', width: 110, render: (_, r) => {
        const isActive = r.reader_mode === 'Active';
        return <Tag color={isActive ? 'gold' : 'volcano'} style={{ borderRadius: 4, fontSize: 11 }}>
          {isActive ? 'Active RFC' : 'HTTP Push'}
        </Tag>;
      }
    },
    { title: 'EVENT', dataIndex: 'event_name', render: v => <Text style={{ fontSize: 12 }}>{v || '—'}</Text> },
    { title: 'LOCATION', dataIndex: 'location_name', render: v => <Text style={{ fontSize: 12 }}>{v || '—'}</Text> },
    {
      title: 'STATUS', dataIndex: 'status', width: 90,
      render: v => (
        <Badge
          status={v === 'online' ? 'success' : v === 'offline' ? 'error' : 'warning'}
          text={<Text style={{ fontSize: 12, textTransform: 'capitalize' }}>{v || 'offline'}</Text>}
        />
      )
    },
    {
      title: 'LAST DATA RECEIVED', dataIndex: 'last_seen', width: 160,
      render: v => v ? <Text style={{ fontSize: 11 }}>{new Date(v).toLocaleString()}</Text> : <Text type="secondary">—</Text>
    },
    {
      title: 'PUSH URL', dataIndex: 'push_url', width: 260,
      render: (v) => v ? (
        <Space size={4}>
          <Text code style={{ fontSize: 10 }}>{v}</Text>
          <Tooltip title="Copy URL">
            <Button size="small" icon={<CopyOutlined />} onClick={() => copyUrl(v)} style={{ borderRadius: 4 }} />
          </Tooltip>
        </Space>
      ) : <Text type="secondary" style={{ fontSize: 11 }}>Configure reader to generate URL</Text>
    },
    {
      title: 'ACTIONS', width: 80, render: (_, r) => (
        <Space size={4}>
          <Tooltip title="Edit in Reader Manager">
            <Button size="small" icon={<EditOutlined />} style={{ borderRadius: 4 }}
              onClick={() => navigate('/reader-manager')} />
          </Tooltip>
          <Tooltip title="Delete">
            <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 4 }} />
          </Tooltip>
        </Space>
      )
    },
  ];

  // ── Live scan columns ─────────────────────────────────────────────
  const scanCols = [
    {
      title: 'RFID TAG', dataIndex: 'rfid_tag', width: 210,
      render: v => <Text code style={{ fontSize: 10, letterSpacing: '0.5px' }}>{v}</Text>
    },
    { title: 'READER', dataIndex: 'reader_name', render: v => <Text style={{ fontSize: 12 }}>{v || '—'}</Text> },
    { title: 'LOCATION', dataIndex: 'location_name', render: v => <Text style={{ fontSize: 12 }}>{v || '—'}</Text> },
    { title: 'EVENT', dataIndex: 'event_name', render: v => <Text style={{ fontSize: 12 }}>{v || '—'}</Text> },
    {
      title: 'RSSI', dataIndex: 'rssi', width: 110,
      render: v => <RssiBar value={v} />
    },
    { title: 'ANTENNA', dataIndex: 'antenna_port', width: 80, render: v => <Text style={{ fontSize: 12 }}>{v || 1}</Text> },
    {
      title: 'TIMESTAMP', dataIndex: 'scanned_at', width: 160,
      render: v => <Text style={{ fontSize: 11 }}>{v ? new Date(v).toLocaleString() : '—'}</Text>
    },
  ];

  const tabItems = [
    {
      key: 'endpoints',
      label: (
        <Space size={6}>
          <ApiOutlined />
          Reader Endpoints
        </Space>
      ),
      children: (
        <div>
          <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
            <Col>
              <Text strong style={{ fontSize: 14, color: '#0F1D6B' }}>Configured Reader Endpoints</Text>
            </Col>
            <Col>
              <Space>
                <Button icon={<ReloadOutlined />} onClick={loadReaders} loading={loadingR} style={{ borderRadius: 6 }}>
                  Refresh
                </Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/reader-manager')}
                  style={{ background: '#0F1D6B', borderRadius: 6 }}>
                  Add Reader Endpoint
                </Button>
              </Space>
            </Col>
          </Row>
          <Table
            columns={readerCols}
            dataSource={readers}
            rowKey="id"
            loading={loadingR}
            size="small"
            scroll={{ x: 1100 }}
            pagination={{ pageSize: 10, showTotal: t => `${t} readers` }}
          />
        </div>
      )
    },
    {
      key: 'stream',
      label: (
        <Space size={6}>
          <ThunderboltOutlined />
          Live Data Stream
        </Space>
      ),
      children: (
        <div>
          {/* Stats */}
          <Row gutter={16} style={{ marginBottom: 20 }}>
            <Col span={8}>
              <Card size="small" style={{ borderRadius: 10, borderLeft: '4px solid #2563eb' }}>
                <Statistic
                  title={<Text style={{ fontSize: 12 }}>Total Scans Today</Text>}
                  value={stats.total_scans_today || 0}
                  prefix={<WifiOutlined style={{ color: '#2563eb' }} />}
                  valueStyle={{ color: '#0F1D6B', fontSize: 28, fontWeight: 700 }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" style={{ borderRadius: 10, borderLeft: '4px solid #9333ea' }}>
                <Statistic
                  title={<Text style={{ fontSize: 12 }}>Unique Tags Today</Text>}
                  value={stats.unique_tags_today || 0}
                  prefix={<span style={{ marginRight: 4 }}>🏷️</span>}
                  valueStyle={{ color: '#9333ea', fontSize: 28, fontWeight: 700 }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small" style={{ borderRadius: 10, borderLeft: '4px solid #16a34a' }}>
                <Statistic
                  title={<Text style={{ fontSize: 12 }}>Active Readers</Text>}
                  value={stats.active_readers || readers.filter(r => r.status === 'online').length}
                  prefix={<CheckCircleOutlined style={{ color: '#16a34a' }} />}
                  valueStyle={{ color: '#16a34a', fontSize: 28, fontWeight: 700 }}
                />
              </Card>
            </Col>
          </Row>

          {/* Controls */}
          <Row align="middle" justify="space-between" style={{ marginBottom: 12 }}>
            <Col>
              <Space>
                <Text style={{ fontSize: 13 }}>Auto refresh</Text>
                <Switch
                  checked={autoRefresh}
                  onChange={setAutoRefresh}
                  checkedChildren="ON"
                  unCheckedChildren="OFF"
                  style={{ background: autoRefresh ? '#16a34a' : undefined }}
                />
              </Space>
            </Col>
            <Col>
              <Space>
                <Button icon={<ReloadOutlined />} onClick={loadScans} loading={loadingS}
                  style={{ borderRadius: 6, borderColor: '#16a34a', color: '#16a34a' }}>
                  Refresh
                </Button>
                <Button
                  icon={streaming ? <StopOutlined /> : <ThunderboltOutlined />}
                  onClick={() => setStreaming(s => !s)}
                  style={{
                    borderRadius: 6,
                    background: streaming ? '#dc2626' : '#0F1D6B',
                    borderColor: streaming ? '#dc2626' : '#0F1D6B',
                    color: '#fff'
                  }}
                >
                  {streaming ? 'Stop' : 'Start'} Stream
                </Button>
              </Space>
            </Col>
          </Row>

          {streaming && (
            <div style={{ marginBottom: 10 }}>
              <Badge status="processing" text={<Text style={{ color: '#16a34a', fontSize: 12 }}>Live stream active — refreshing every 2s</Text>} />
            </div>
          )}

          <Table
            columns={scanCols}
            dataSource={scans}
            rowKey="id"
            loading={loadingS}
            size="small"
            scroll={{ x: 1000 }}
            pagination={{ pageSize: 15, showTotal: t => `${t} scans` }}
          />
        </div>
      )
    }
  ];

  return (
    <div style={{ background: '#f5f6fa', minHeight: '100vh', padding: '0 0 24px' }}>
      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #eee', padding: '16px 24px 12px' }}>
        <Row align="middle" justify="space-between">
          <Col>
            <Title level={4} style={{ margin: 0, color: '#0F1D6B' }}>
              <ApiOutlined style={{ marginRight: 8 }} />Reader API
            </Title>
            <Text type="secondary" style={{ fontSize: 12 }}>Manage RFID reader connections and monitor live scan data</Text>
          </Col>
          <Col>
            <Select value={filterEvent} onChange={setFilterEvent}
              style={{ width: 200 }} placeholder="Filter by Event" allowClear>
              {events.map(e => <Option key={e.id} value={e.id}>{e.event_name}</Option>)}
            </Select>
          </Col>
        </Row>
      </div>

      <div style={{ padding: '20px 24px 0' }}>
        <Card style={{ borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={tabItems}
            tabBarStyle={{ marginBottom: 20 }}
          />
        </Card>
      </div>
    </div>
  );
}
